import * as actions from 'client/actions/actionsManagement'
import * as entityActions from 'client/actions/entityActions'
import OldEntitySettings from 'client/components/core/Sidebar/components/Settings/old-entity-settings'
import { useIsShowAdditionalAction } from 'client/hooks/use-is-show-additional-action'
import useManagement from 'client/hooks/useManagement'
import { createPage } from 'client/pages'
import { isSettingsOverlayVisible } from 'client/reducers/managementReducer'
import { usePage } from 'client/store'
import pageSelectors from 'client/store/page/pageSelectors'
import EntityTypeEnum from 'common/enums/entityTypeEnum'
import { useActions } from 'common/hooks/useActions'
import EntityInterface from 'common/types/entities/EntityInterface'
import { OldEntityInterface } from 'common/types/entities/OldEntityInterface'
import React from 'react'
import { useDispatch } from 'react-redux'
import { useIsAllowedWebsiteHeaderEntity } from 'src/editor/hooks/use-is-allowed-website-header-entity'
import { BackButton } from 'src/editor/shared/components/back-button'
import { Overlay } from 'src/shared/components/overlay'
import { LibraryElementEnum } from 'src/shared/enums/LibraryElementEnum'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'
import { useRemoveEntityMobileStyle } from '../hooks/use-remove-entity-mobile-style'
import { useRemoveEntityStyle } from '../hooks/use-remove-entity-style'
import { useUpdateEntityStyle } from '../hooks/use-update-entity'
import {
  useOverlayActionsContext,
  useOverlayVisibilityContext,
} from '../settings-overlay-context'
import { isLockedDescendant } from '../utils/is-locked-descendant'
import { EntitySettingsName } from './entity-settings-name'
import { mapEntityTypeToLibraryElement } from './map-library-element-to-entity-type'

interface EntitySettingsProps {
  entity: EntityInterface
}

export const EntitySettings = ({ entity }: EntitySettingsProps) => {
  const { t } = useTypedTranslation()
  const dispatch = useDispatch()
  const isColorPickerOverlay = useManagement(isSettingsOverlayVisible)
  const isOverlayVisible = useOverlayVisibilityContext()
  const { close, toggle } = useOverlayActionsContext()
  const updateEntityStyle = useUpdateEntityStyle(
    dispatch,
    entity as unknown as OldEntityInterface,
  )
  const removeEntityStyle = useRemoveEntityStyle(
    dispatch,
    entity as unknown as OldEntityInterface,
  )
  const removeEntityMobileStyle = useRemoveEntityMobileStyle(
    dispatch,
    entity as unknown as OldEntityInterface,
  )
  const moveDown = useActions(entityActions.moveDown)
  const moveUp = useActions(entityActions.moveUp)

  function handleMoveUp(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
    e.stopPropagation()
    moveUp(entity)
  }

  function handleMoveDown(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
    e.stopPropagation()
    moveDown(entity)
  }

  const [showCreateBlockDialog, togglePickerOverlay, toggleSettings] =
    useActions([
      actions.showCreateBlockDialog,
      actions.toggleSettingsOverlay,
      actions.toggleSettings,
    ])
  const copy = useActions(entityActions.copy)
  const remove = useActions(entityActions.remove)
  const pageType = usePage(pageSelectors.getPageType)
  const parentEntity = usePage(page =>
    pageSelectors.getEntityById(
      page,
      ('parentId' in entity && entity.parentId) || null,
    ),
  )
  const entityParents = usePage(page =>
    pageSelectors.getEntityParents(page.entities, entity),
  )

  const isShowAdditionalAction = useIsShowAdditionalAction({
    entityParents,
    entity: entity as { type: EntityTypeEnum },
  })

  const { createLibraryElement, createEntitySettings } = createPage(pageType)
  const isMasterBlock =
    'masterBlockId' in entity && Boolean(entity.masterBlockId)
  const isLocked =
    parentEntity &&
    isLockedDescendant(
      entity.type as EntityTypeEnum,
      parentEntity.type as EntityTypeEnum,
    )
  const isNotPopupEntity = entity.type !== EntityTypeEnum.Popup
  const isAllowedWebsiteHeaderEntity = useIsAllowedWebsiteHeaderEntity(
    ('parentId' in entity && entity.parentId) || undefined,
    entity.type as EntityTypeEnum,
  )
  const isMoveAllowed =
    isNotPopupEntity &&
    ![EntityTypeEnum.WebsiteFooter, EntityTypeEnum.WebsiteHeader].includes(
      entity.type as EntityTypeEnum,
    ) &&
    !isAllowedWebsiteHeaderEntity
  const isSaveAllowed = isNotPopupEntity && !isMasterBlock
  const isCopyAllowed =
    isNotPopupEntity &&
    !('isMasterBlockRoot' in entity && entity.isMasterBlockRoot)
  const isRemoveAllowed =
    isNotPopupEntity && entity.type !== EntityTypeEnum.PriceElement

  const safeRemove = () =>
    confirm(
      t(
        isMasterBlock
          ? 'components.core.base_entity.action_box.safe_remove_master'
          : 'components.core.base_entity.action_box.safe_remove',
      ),
    ) && remove(entity)

  const hide = () => {
    close()
    togglePickerOverlay(false)
  }

  return (
    <>
      <div className="mb-6 flex items-center justify-between">
        <BackButton onClick={() => toggleSettings(null)}>
          {t('entity_settings.back_to_page')}
        </BackButton>

        <div className="flex items-center gap-2.5">
          {isMoveAllowed && (
            <button
              type="button"
              className="cursor-pointer text-L"
              onClick={handleMoveUp}
            >
              <i className="fas fa-arrow-up text-gray-700" />
            </button>
          )}
          {isMoveAllowed && (
            <button
              type="button"
              className="cursor-pointer text-L"
              onClick={handleMoveDown}
            >
              <i className="fas fa-arrow-down text-gray-700" />
            </button>
          )}
          {!isLocked && isShowAdditionalAction && (
            <>
              {isCopyAllowed && (
                <button
                  type="button"
                  title={t('components.core.actions.copy')}
                  className="cursor-pointer text-L"
                  onClick={() => copy({ entity, t })}
                >
                  <i className="far fa-copy text-gray-700" />
                </button>
              )}
              {isSaveAllowed && (
                <button
                  type="button"
                  title={t('components.core.dialog.save_block.header')}
                  className="cursor-pointer text-L"
                  onClick={() => showCreateBlockDialog(entity.id)}
                >
                  <i className="far fa-save text-gray-700" />
                </button>
              )}
              {isRemoveAllowed && (
                <button
                  type="button"
                  title={t('components.core.actions.remove')}
                  className="cursor-pointer text-L"
                  onClick={safeRemove}
                >
                  <i className="far fa-trash-alt text-gray-700" />
                </button>
              )}
            </>
          )}
        </div>
      </div>
      <EntitySettingsName entity={entity} />
      <div>
        {entity.type in EntityTypeEnum ||
        entity.type === EntityTypeEnum.Carousel ||
        entity.type === EntityTypeEnum.CarouselItem ? (
          createEntitySettings(
            entity,
            createLibraryElement(
              mapEntityTypeToLibraryElement[entity.type] as LibraryElementEnum,
            ),
          )
        ) : (
          <OldEntitySettings
            entity={entity}
            isOverlayActive={isOverlayVisible}
            toggleSettingsOverlay={toggle}
            updateEntityStyle={updateEntityStyle}
            removeEntityStyle={removeEntityStyle}
            removeEntityMobileStyle={removeEntityMobileStyle}
          />
        )}
        {(isOverlayVisible || isColorPickerOverlay) && (
          <Overlay hide={hide} zIndex="var(--z-index-picker)" />
        )}
      </div>
    </>
  )
}
